// src/components/FindDoctorPage/MapSection/MapSection.jsx
"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./MapSection.css";
import DoctorInfoCard from "../DoctorInfoCard/DoctorInfoCard";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// ─── Leaflet icons ────────────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});
const SH = "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png";
const mkIcon = (color) => new L.Icon({
  iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
  shadowUrl: SH, iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});
const ICONS = { blue: mkIcon("blue"), green: mkIcon("green"), red: mkIcon("red") };

const createUserLocationIcon = (photo) => L.divIcon({
  className: "custom-user-marker",
  html: `<div class="user-location-marker">
    <div class="pulse-circle"></div><div class="pulse-circle-2"></div>
    <div class="user-avatar-wrapper">
      <img src="${photo}" alt="You" class="user-avatar"
        onerror="this.onerror=null;this.src='https://cdn-icons-png.flaticon.com/512/149/149071.png';"/>
    </div></div>`,
  iconSize: [80, 80], iconAnchor: [40, 40],
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const normalizeToLatLng = (loc) => {
  if (!loc) return null;
  if (Array.isArray(loc)) { const [a, b] = loc; return a == null || b == null ? null : [a, b]; }
  if (typeof loc === "object") {
    const lat = loc.lat ?? loc.latitude, lng = loc.lng ?? loc.longitude;
    return lat == null || lng == null ? null : [lat, lng];
  }
  return null;
};

const cleanId = (v) => {
  if (v == null) return null;
  const s = String(v).trim().replace(/^(dr_|pat_)/i, "").trim().toLowerCase();
  return ["", "nan", "undefined", "null"].includes(s) ? null : s;
};

// ─── Sous-composants ──────────────────────────────────────────────────────────
const MapController = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => { const c = normalizeToLatLng(center); if (c) map.setView(c, zoom); }, [center, zoom, map]);
  return null;
};

const MapControls = ({ onZoomIn, onZoomOut, onLocate }) => (
  <div className="map-controls">
    <button className="map-control-btn" onClick={onZoomIn} type="button">+</button>
    <button className="map-control-btn" onClick={onZoomOut} type="button">−</button>
    <button className="map-control-btn locate" onClick={onLocate} type="button">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
        <path d="M10 2v3M10 15v3M2 10h3M15 10h3" stroke="currentColor" strokeWidth="2"/>
      </svg>
    </button>
  </div>
);

const DoctorMarker = ({ doctor, onHover, onLeave, onClick, isSelected }) => {
  const ref = useRef(null);
  const icon = isSelected ? ICONS.red : doctor.isMedilinker ? ICONS.green : ICONS.blue;
  return (
    <Marker ref={ref} position={[doctor.lat, doctor.lng]} icon={icon} eventHandlers={{
      mouseover: () => onHover(doctor),
      mouseout:  () => onLeave(),
      click:     () => onClick(doctor),
    }}/>
  );
};

export const NotMedilinkerPopup = ({ doctor, onClose }) => (
  <div className="not-medilinker-overlay" onClick={onClose}>
    <div className="not-medilinker-popup" onClick={(e) => e.stopPropagation()}>
      <button className="not-medilinker-close" onClick={onClose}>×</button>
      <div className="not-medilinker-icon">
        <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
          <circle cx="26" cy="26" r="24" stroke="#F59E0B" strokeWidth="2.5" fill="#FFFBEB"/>
          <path d="M26 16v13" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round"/>
          <circle cx="26" cy="36" r="2.5" fill="#F59E0B"/>
        </svg>
      </div>
      <h3 className="not-medilinker-title">Not on MediLink</h3>
      <p className="not-medilinker-message">
        Sorry, <strong>{doctor?.name || doctor?.fullName || "this doctor"}</strong>{" "}
        doesn't have a MediLink account yet.
      </p>
      <p className="not-medilinker-sub">You can contact them directly to book an appointment.</p>
      <button className="not-medilinker-btn" onClick={onClose}>Got it</button>
    </div>
  </div>
);

const MapLegend = ({ medilinkerCount, externalCount }) => (
  <div className="map-legend">
    <div className="map-legend-item">
      <span className="map-legend-dot map-legend-dot--green"/>
      <span>MediLink doctor</span>
    </div>
    <div className="map-legend-item">
      <span className="map-legend-dot map-legend-dot--blue"/>
      <span>External doctor</span>
    </div>
  </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const MapSection = ({
  searchQuery, location, specialty,
  selectedDoctor, setSelectedDoctor,
  doctors = [], userLocation, focusLocation,
  loading, error,
  currentUser, avatarSrc,
  requestLocation,
}) => {
  const { t }    = useTranslation();
  const navigate = useNavigate();

  // ── Enrichissement simplifié — isMedilinker déjà défini par DoctorFinder ──
  const enrichedDoctors = useMemo(() => {
    return doctors.map((doc) => {
      if (doc.isMedilinker === true) {
        return {
          ...doc,
          medilinkerAccountId: doc.medilinkerAccountId || cleanId(doc.id) || null,
        };
      }
      return {
        ...doc,
        isMedilinker: false,
        medilinkerData: null,
        medilinkerAccountId: null,
      };
    });
  }, [doctors]);

  const medilinkerCount = useMemo(() => enrichedDoctors.filter((d) => d.isMedilinker).length, [enrichedDoctors]);
  const externalCount   = useMemo(() => enrichedDoctors.filter((d) => !d.isMedilinker).length, [enrichedDoctors]);

  // ── Map state ──────────────────────────────────────────────────────────────
  const initCenter = normalizeToLatLng(userLocation) || [35.8256, 10.6341];
  const [mapCenter,           setMapCenter]           = useState(initCenter);
  const [mapZoom,             setMapZoom]             = useState(normalizeToLatLng(userLocation) ? 12 : 8);
  const [mapInstance,         setMapInstance]         = useState(null);
  const [hoveredDoctor,       setHoveredDoctor]       = useState(null);
  const [clickedDoctor,       setClickedDoctor]       = useState(null);
  const [notMedilinkerDoctor, setNotMedilinkerDoctor] = useState(null);
  const [internalUserLocation,setInternalUserLocation]= useState(normalizeToLatLng(userLocation));

  const userPhoto = avatarSrc || currentUser?.avatar || currentUser?.avatarUrl || currentUser?.photo
    || "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  useEffect(() => {
    const c = normalizeToLatLng(userLocation);
    if (c) { setInternalUserLocation(c); setMapCenter(c); setMapZoom(12); }
  }, [userLocation]);

  useEffect(() => {
    const center = normalizeToLatLng(focusLocation) || normalizeToLatLng(userLocation);
    if (center) { setMapCenter(center); setMapZoom(12); }
  }, [focusLocation, userLocation]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleHover = (doc) => { if (!clickedDoctor) setHoveredDoctor(doc); };
  const handleLeave = ()    => { if (!clickedDoctor) setHoveredDoctor(null); };
  const handleClick = (doc) => {
    setHoveredDoctor(null); setClickedDoctor(doc);
    setMapCenter([doc.lat, doc.lng]); setMapZoom(14);
    setSelectedDoctor?.(doc);
  };
  const handleBook = (doc) => {
    if (doc.isMedilinker) {
      const id = doc.medilinkerAccountId || doc.id;
      if (id) navigate(`/doctor-details/${id}?tab=schedule`);
    } else {
      setNotMedilinkerDoctor(doc);
    }
  };
  const handleCloseCard = () => { setClickedDoctor(null); setHoveredDoctor(null); setSelectedDoctor?.(null); };

  const displayedDoctor = clickedDoctor || hoveredDoctor;

  return (
    <div className="map-section">
      <div className="map-container">
        <MapContainer center={mapCenter} zoom={mapZoom}
          style={{ height:"100%", width:"100%" }} className="leaflet-map" whenCreated={setMapInstance}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapController center={mapCenter} zoom={mapZoom}/>

          {internalUserLocation && (
            <Marker position={internalUserLocation}
              icon={createUserLocationIcon(userPhoto)} zIndexOffset={1000}/>
          )}

          {enrichedDoctors.filter((d) => d.lat != null && d.lng != null).map((doc) => (
            <DoctorMarker key={doc.id} doctor={doc}
              onHover={handleHover} onLeave={handleLeave}
              onClick={handleClick} isSelected={clickedDoctor?.id === doc.id}
            />
          ))}

                  <MapControls
                    onZoomIn={() => mapInstance?.zoomIn()}
                    onZoomOut={() => mapInstance?.zoomOut()}
                    onLocate={() => {
                      if (internalUserLocation) {
                        setMapCenter(internalUserLocation);
                        setMapZoom(12);
                      } else if (typeof requestLocation === "function") {
                        // Trigger parent location request which will prompt the browser
                        try {
                          requestLocation({ specialty, governorate: location, radius: undefined });
                        } catch (e) {
                          console.warn("MapSection: requestLocation failed", e);
                        }
                      } else {
                        // As a fallback, try the browser geolocation directly
                        if (navigator && navigator.geolocation) {
                          navigator.geolocation.getCurrentPosition((p) => {
                            const loc = [p.coords.latitude, p.coords.longitude];
                            setInternalUserLocation(loc);
                            setMapCenter(loc);
                            setMapZoom(12);
                          }, (err) => {
                            console.warn("MapSection direct geolocation failed", err);
                          });
                        }
                      }
                    }}
                  />
        </MapContainer>

        <MapLegend medilinkerCount={medilinkerCount} externalCount={externalCount}/>

        {displayedDoctor && (
          <div className={`doctor-info-card-wrapper ${clickedDoctor ? "persistent" : ""}`}>
            <DoctorInfoCard
              doctor={displayedDoctor}
              showClose={!!clickedDoctor}
              onClose={handleCloseCard}
              isMedilinker={displayedDoctor.isMedilinker}
              onAppointmentClick={() => handleBook(displayedDoctor)}
            />
          </div>
        )}

        {notMedilinkerDoctor && (
          <NotMedilinkerPopup doctor={notMedilinkerDoctor} onClose={() => setNotMedilinkerDoctor(null)}/>
        )}

        {loading && (
          <div className="loading-location">
            <div className="loading-spinner"/>
            <p>{t("doctorFinder.messages.loadingNearby")}</p>
          </div>
        )}
        {error && !loading && <div className="loading-location error"><p>{error}</p></div>}
      </div>
    </div>
  );
};

export default MapSection;